import { useCallback, useRef } from "react";

import { useParallelDirectUploads } from "@/react/hooks/useParallelDirectUploads";
import type { FileUploadState } from "@/react/hooks/useParallelDirectUploads";

import type { Crop, SelectedImage } from "../../store";
import { useImagePostModalStore } from "../../store";

interface CroppedImageResult {
  croppedArea: Crop;
  croppedImage: File;
}
const cropImage = (
  image: SelectedImage,
  galleryAspectRatio: number,
): Promise<CroppedImageResult> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let croppedAreaPixels = image.croppedAreaPixels;

    if (!ctx) {
      reject(new Error("Canvas context could not be created"));
      return;
    }

    if (croppedAreaPixels) {
      // Discard any cropping if the aspect ratio is too different from the gallery aspect ratio
      // This happens when the user crops an image, then changes the gallery aspect ratio, so
      // the image needs to be re-cropped with the default cropping area (setting undefined)
      const { width, height } = croppedAreaPixels;
      const aspectRatio = parseFloat((width / height).toFixed(2));
      if (aspectRatio !== parseFloat(galleryAspectRatio.toFixed(2))) {
        croppedAreaPixels = undefined;
      }
    }

    const objectUrl = URL.createObjectURL(image.file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const croppedArea =
        croppedAreaPixels ||
        defaultCenteredCroppedArea(img, galleryAspectRatio);
      canvas.width = croppedArea.width;
      canvas.height = croppedArea.height;

      ctx.drawImage(
        img,
        croppedArea.x,
        croppedArea.y,
        croppedArea.width,
        croppedArea.height,
        0,
        0,
        croppedArea.width,
        croppedArea.height,
      );

      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error("Image could not be cropped"));
          return;
        }

        const croppedImage = new File([blob], image.file.name, {
          type: "image/jpeg",
          lastModified: new Date().getTime(),
        });
        resolve({ croppedImage, croppedArea });
      }, "image/jpeg");
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Image could not be loaded"));
    };
    img.src = objectUrl;
  });

const defaultCenteredCroppedArea = (
  img: HTMLImageElement,
  targetAspectRatio: number,
) => {
  const originalAspectRatio = img.width / img.height;

  let x, y, width, height;

  if (originalAspectRatio > targetAspectRatio) {
    // Keep the height, adjust the width
    width = Math.round(img.height * targetAspectRatio);
    height = img.height;
    x = Math.round((img.width - width) / 2);
    y = 0;
  } else {
    // Keep the width, adjust the height
    width = img.width;
    height = Math.round(img.width / targetAspectRatio);
    x = 0;
    y = Math.round((img.height - height) / 2);
  }

  return { x, y, width, height };
};

export const useUploadImages = () => {
  const croppedAreasRef = useRef<Crop[]>([]);
  const {
    selectedFiles,
    setSelectedFiles,
    galleryAspectRatio,
    setUploadedImages,
    addUploadedImages,
    uploadedImages,
    setIsImagesDirty,
  } = useImagePostModalStore();
  const { status, overallProgress, reset, fileStates, uploadFiles } =
    useParallelDirectUploads();

  const uploadImages = () => {
    if (!galleryAspectRatio) {
      throw new Error("Gallery aspect ratio not set");
    }
    const croppingPromises = selectedFiles.map(file =>
      cropImage(file, galleryAspectRatio),
    );

    Promise.all(croppingPromises)
      .then(croppedImageResults => {
        croppedAreasRef.current = croppedImageResults.map(
          result => result.croppedArea,
        );
        setSelectedFiles(
          selectedFiles.map((file, index) => ({
            ...file,
            croppedAreaPixels: croppedImageResults[index].croppedArea,
          })),
        );
        uploadFiles(croppedImageResults.map(result => result.croppedImage));
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(`Error processing images: ${message}`);
      });
  };

  const getCroppedArea = useCallback(
    (index: number) => {
      const croppedArea =
        croppedAreasRef.current[index] ||
        selectedFiles[index]?.croppedAreaPixels;

      if (!croppedArea) {
        throw new Error("Cropped area not found");
      }

      return croppedArea;
    },
    [selectedFiles],
  );

  const buildUploadedImage = (
    fileState: FileUploadState,
    croppedArea: Crop,
  ) => ({
    signed_id: fileState.response.signed_id,
    url: fileState.response.blob_url,
    original_url: fileState.response.blob_url,
    filename: fileState.response.filename,
    width: croppedArea.width,
    height: croppedArea.height,
  });

  const updateStoredImages = useCallback(
    (editingImageSignedId?: string) => {
      if (editingImageSignedId) {
        const fileState = fileStates[0];

        if (!fileState) {
          throw new Error("Uploaded image not found");
        }

        const editedImage = buildUploadedImage(fileState, getCroppedArea(0));
        setUploadedImages(
          uploadedImages.map(image =>
            image.signed_id === editingImageSignedId
              ? {
                  ...image,
                  ...editedImage,
                }
              : image,
          ),
        );
      } else {
        addUploadedImages(
          fileStates.map((file, index) =>
            buildUploadedImage(file, getCroppedArea(index)),
          ),
        );
      }
      setIsImagesDirty(true);
    },
    [
      addUploadedImages,
      fileStates,
      getCroppedArea,
      setIsImagesDirty,
      setUploadedImages,
      uploadedImages,
    ],
  );

  return {
    uploadImages,
    overallProgress,
    resetUploads: reset,
    status,
    fileStates,
    updateStoredImages,
  };
};
